import React, { useState, useEffect } from 'react';
import { colors } from './TabNavigation';
import { LogoA, LogoE } from './Logo';
import useIsMobile from './useIsMobile';

function Sidebar({ activeTab, onTabChange, isOpen, onClose }) {
  const isMobile = useIsMobile();
  const [transitsExpanded, setTransitsExpanded] = useState(false);

  // Auto-expand Transits when a Transits subtab is active
  useEffect(() => {
    if (activeTab === 'calendar' || activeTab === 'year-calendar') {
      setTransitsExpanded(true);
    }
  }, [activeTab]);

  // Close sidebar when tab changes
  const handleTabChange = (tabId) => {
    onTabChange(tabId);
    onClose();
  };

  const handleTransitsClick = () => {
    setTransitsExpanded(!transitsExpanded);
  };

  const mainItems = [
    { id: 'calculator', label: 'Birth Chart', icon: '♈', iconComponent: LogoA },
    { id: 'transits', label: 'Transits', icon: '♅', iconComponent: LogoE, hasSubtabs: true },
    { id: 'compatibility', label: 'Compatibility', icon: '♡' },
  ];

  const transitSubtabs = [
    { id: 'calendar', label: "Today's Sky" },
    { id: 'year-calendar', label: 'Transit Calendar' },
  ];

  return (
    <>
      {/* Backdrop overlay */}
      {isOpen && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            zIndex: 99,
            animation: 'fadeIn 0.3s ease',
          }}
        />
      )}

      {/* Sidebar */}
      <div
        style={{
          position: 'fixed',
          right: 0,
          top: 0,
          width: '220px',
          height: '100vh',
          backgroundColor: colors.creamBg,
          borderLeft: `1px solid ${colors.borderSubtle}`,
          padding: '32px 0',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          zIndex: 100,
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease',
        }}
      >
      {/* Logo/Title */}
      <button
        onClick={() => { onTabChange('home'); onClose(); }}
        style={{
          padding: '0 24px',
          marginBottom: '40px',
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: '24px',
          fontWeight: '400',
          color: colors.textPrimary,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        Cuspli
      </button>

      {/* Main Navigation Items */}
      <nav style={{ flex: 1 }}>
        {mainItems.map((item) => {
          const isActive =
            item.id === 'transits'
              ? activeTab === 'calendar' || activeTab === 'year-calendar'
              : activeTab === item.id;

          if (item.hasSubtabs) {
            return (
              <div key={item.id}>
                {/* Transits Main Item */}
                <button
                  onClick={handleTransitsClick}
                  style={{
                    width: '100%',
                    padding: '14px 24px',
                    minHeight: '44px',
                    backgroundColor: isActive ? 'rgba(201, 149, 110, 0.08)' : 'transparent',
                    borderLeft: isActive ? `4px solid ${colors.gold}` : '4px solid transparent',
                    border: 'none',
                    color: isActive ? colors.textPrimary : colors.textSecondary,
                    fontSize: isMobile ? '16px' : '14px',
                    fontWeight: '500',
                    letterSpacing: '0.5px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(26, 20, 16, 0.04)';
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {item.iconComponent ? (
                      <item.iconComponent size={20} color={isActive ? colors.textPrimary : colors.textSecondary} />
                    ) : (
                      <span style={{ fontSize: '16px' }}>{item.icon}</span>
                    )}
                    {item.label}
                  </span>
                  <span
                    style={{
                      fontSize: '12px',
                      transform: transitsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                    }}
                  >
                    ▼
                  </span>
                </button>

                {/* Transits Subtabs */}
                {transitsExpanded && (
                  <div>
                    {transitSubtabs.map((subtab) => {
                      const isSubtabActive = activeTab === subtab.id;
                      return (
                        <button
                          key={subtab.id}
                          onClick={() => handleTabChange(subtab.id)}
                          style={{
                            width: '100%',
                            padding: '12px 24px 12px 44px',
                            minHeight: '44px',
                            backgroundColor: isSubtabActive ? 'rgba(201, 149, 110, 0.08)' : 'transparent',
                            borderLeft: isSubtabActive ? `4px solid ${colors.gold}` : '4px solid transparent',
                            border: 'none',
                            color: isSubtabActive ? colors.textPrimary : colors.textSecondary,
                            fontSize: isMobile ? '15px' : '13px',
                            fontWeight: '400',
                            letterSpacing: '0.3px',
                            textAlign: 'left',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                          }}
                          onMouseEnter={(e) => {
                            if (!isSubtabActive) e.currentTarget.style.backgroundColor = 'rgba(26, 20, 16, 0.04)';
                          }}
                          onMouseLeave={(e) => {
                            if (!isSubtabActive) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          {subtab.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          // Regular main item
          return (
            <button
              key={item.id}
              onClick={() => {
                handleTabChange(item.id);
                setTransitsExpanded(false);
              }}
              style={{
                width: '100%',
                padding: '14px 24px',
                minHeight: '44px',
                backgroundColor: isActive ? 'rgba(201, 149, 110, 0.08)' : 'transparent',
                borderLeft: isActive ? `4px solid ${colors.gold}` : '4px solid transparent',
                border: 'none',
                color: isActive ? colors.textPrimary : colors.textSecondary,
                fontSize: isMobile ? '16px' : '14px',
                fontWeight: '500',
                letterSpacing: '0.5px',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(26, 20, 16, 0.04)';
              }}
              onMouseLeave={(e) => {
                if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {item.iconComponent ? (
                <item.iconComponent size={20} color={isActive ? colors.textPrimary : colors.textSecondary} />
              ) : (
                <span style={{ fontSize: '16px' }}>{item.icon}</span>
              )}
              {item.label}
            </button>
          );
        })}
      </nav>
      </div>
    </>
  );
}

export default Sidebar;
